import React, { useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { TextCss } from "../../components/GlobalCss/TextCss";
import { formatNumberForInput } from "../../utils/helper";
import "./Form.css";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";

interface CurrencyInputProps {
  name: string;
  label?: string;
  required?: boolean;
  symbol?: string; // default PKR
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  requiredMessage?: string;
  allowDecimals?: boolean;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  name,
  label = "",
  required = false,
  symbol = "$",
  disabled = false,
  placeholder = "0.00",
  className = "",
  requiredMessage,
  allowDecimals = true,
}) => {
  const {
    control,
    formState: { isSubmitted },
    trigger,
  } = useFormContext();

  symbol = useCurrencySymbol();

  const [limitErrorVisible, setLimitErrorVisible] = useState(false);
  const [decimalErrorVisible, setDecimalErrorVisible] = useState(false);
  const MAX_LIMIT = 99999999.99;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    const allowedKeys = [
      "Backspace",
      "Tab",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(key)) return;
    if (e.ctrlKey || e.metaKey) return;

    const isNumber = /^[0-9]$/.test(key);
    const isDot = key === ".";

    if (isDot) {
      if (!allowDecimals) {
        setDecimalErrorVisible(true);
        setTimeout(() => setDecimalErrorVisible(false), 2000);
        e.preventDefault();
      } else if ((e.target as HTMLInputElement).value.includes(".")) {
        e.preventDefault();
      }
      return;
    }

    if (!isNumber) e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    const regex = allowDecimals ? /^[0-9.,]+$/ : /^[0-9,]+$/;
    
    if (!regex.test(text)) {
      if (!allowDecimals && text.includes(".")) {
        setDecimalErrorVisible(true);
        setTimeout(() => setDecimalErrorVisible(false), 2000);
      }
      e.preventDefault();
    }
  };

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className={TextCss.black.sub_heading_600_14}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <div
        className="currency-input-wrapper"
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: "10px",
            color: "var(--Grey-Subtext1)",
            fontWeight: 500,
            pointerEvents: "none",
          }}
        >
          {symbol}
        </span>

        <Controller
          control={control}
          name={name}
          rules={{
            required: required
              ? requiredMessage || `${label} is required`
              : false,
          }}
          render={({
            field: { onChange, value, onBlur, ref },
            fieldState: { error, isTouched },
          }) => {
            const currentError = (isTouched || isSubmitted) && error ? error.message : undefined;
            // Validate on blur explicitly rather than relying on the parent
            // form's `mode` (most forms use the RHF default "onSubmit",
            // which never populates `fieldState.error` until a submit
            // attempt — and Save stays disabled while the form is invalid,
            // so that submit can never happen).
            const handleBlur = () => {
              onBlur();
              trigger(name);
            };

            return (
              <input
                type="text"
                disabled={disabled}
                placeholder={placeholder}
                className={`form-control form-control-currency ${currentError ? "is-invalid" : ""}`}
                style={{ paddingLeft: "35px" }} // space for symbol
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={handleBlur}
                value={formatNumberForInput(value?.toString() || "")}
                onChange={(e) => {
                  const rawVal = e.target.value.replace(/,/g, "");

                  // --- Validation Logic ---

                  // 1. Decimal restriction
                  if (!allowDecimals && rawVal.includes(".")) {
                    setDecimalErrorVisible(true);
                    setTimeout(() => setDecimalErrorVisible(false), 2000);
                    return;
                  }
                  if (rawVal.includes(".") && rawVal.split(".")[1].length > 2) {
                    return;
                  }

                  // 2. Max Value hard-block + immediate error
                  const numericVal = parseFloat(rawVal);
                  if (!isNaN(numericVal) && numericVal > MAX_LIMIT) {
                    setLimitErrorVisible(true);
                    setTimeout(() => {
                      setLimitErrorVisible(false);
                    }, 1000);
                    return;
                  }

                  onChange(rawVal);

                  // Must run unconditionally, not just "if (error)" --
                  // react-hook-form's formState.isValid lags a full render
                  // behind field.onChange under mode: "onChange" (a known
                  // RHF quirk), so a Save button gated on isValid stays
                  // disabled for the keystroke that actually made the form
                  // valid, only catching up on the *next* interaction.
                  // Calling trigger() here forces isValid to resolve in the
                  // same interaction.
                  trigger(name);
                }}
                ref={ref}
              />
            );
          }}
        />
      </div>

      {limitErrorVisible && (
        <div className="invalid-feedback" style={{ display: "block" }}>
          Amount cannot exceed {formatNumberForInput(MAX_LIMIT.toString())}
        </div>
      )}

      {decimalErrorVisible && (
        <div className="invalid-feedback" style={{ display: "block" }}>
          Decimals are not allowed
        </div>
      )}

      <Controller
        control={control}
        name={name}
        render={({ fieldState: { error, isTouched } }) =>
          !limitErrorVisible && !decimalErrorVisible && (isTouched || isSubmitted) && error ? (
            <div className="invalid-feedback" style={{ display: "block" }}>
              {error.message}
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default CurrencyInput;
